import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

// --- Operators ---

// `at_least` is for ordered select fields (Priority ≥ High, design 9a).
export type SelectOperator = 'includes' | 'excludes' | 'at_least'
export type TextOperator = 'equals' | 'not_equal' | 'contains' | 'does_not_contain'
export type NumberOperator = 'equals' | 'not_equal' | 'greater_than' | 'less_than'
// `on` / `before` / `after` with a day-start timestamp (the Due field's typed
// `due:<fri` form, design 9b).
export type DateOperator = 'equals' | 'before' | 'after' | 'on'
export type FilterOperator = SelectOperator | TextOperator | NumberOperator | DateOperator

// --- Field types ---

export type FilterFieldType = 'select' | 'text' | 'number' | 'date'

// --- Field keys (all supported filterable properties) ---

export type BuiltInFilterFieldKey =
  | 'status'
  | 'priority'
  | 'assignee'
  | 'createdBy'
  | 'due'
  | 'labels'
  | 'name'
  | 'project'
  | 'cycle'
export type FilterFieldKey = BuiltInFilterFieldKey | `cf_${string}`

// --- Filter value ---

export type FilterValue = string | number

// --- A single filter instance ---

export interface TaskFilter {
  /** Store-only handle for a chip; never saved (see serializeSavedFilterConfig). */
  id?: string
  fieldKey: FilterFieldKey
  operator: FilterOperator
  values: Array<FilterValue>
  /**
   * OR group (design 9b): a task matches when it passes every filter in at
   * least one group. Filters without a group apply to all groups, so saved
   * views from before groups (and pinned filters) behave exactly as before.
   */
  group?: number
}

// --- Field definition (metadata for each filterable property) ---

export interface FilterFieldDefinition {
  key: FilterFieldKey
  label: string
  /** How the property reads in the Filter menu, when that differs from `label`. */
  menuLabel?: string
  pluralLabel: string
  fieldType: FilterFieldType
  icon: React.ComponentType<{ className?: string }>
  operators: Array<FilterOperator>
  defaultOperator: FilterOperator
  getTaskValue: (task: TaskDocument) => FilterValue | Array<FilterValue> | null
  requiresJoinData?: boolean
}

// --- Helpers ---

export const EMPTY_FILTERS: Array<TaskFilter> = []

export function isFiltersEmpty(filters: Array<TaskFilter>): boolean {
  return filters.length === 0 || filters.every((f) => f.values.length === 0)
}

export function getActiveFilterCount(filters: Array<TaskFilter>): number {
  return filters.filter((f) => f.values.length > 0).length
}
